import React, { useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import NumberFormat, { NumberFormatProps, NumberFormatValues, SourceInfo } from 'react-number-format';
import { Control } from 'react-hook-form/dist/types/form';
import { InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { UseControllerProps } from 'react-hook-form/dist/types/controller';

interface Props<FormValues extends FieldValues = FieldValues> extends Omit<
  NumberFormatProps,
  'value' | 'onChange' | 'onValueChange'
> {
  label?: string;
  dataTestId?: string;
  control: Control<FormValues>;
  textFieldProps?: TextFieldProps;
  name: UseControllerProps<FormValues>['name'];
  customOnChange?: (nextValue: number, name: UseControllerProps<FormValues>['name']) => void;
  /**
   * Edit the bare number: decimals are not padded while focused and the contents are selected on focus,
   * so typing replaces the value. Padding (e.g. 7 -> 7.00) is applied on blur.
   */
  formatOnBlur?: boolean;
  /** Render % as a fixed end adornment instead of a suffix inside the editable text. */
  unitAdornment?: boolean;
}

export const InterestInputField = <FormValues extends FieldValues = FieldValues>({
  name,
  label,
  control,
  customOnChange,
  dataTestId,
  formatOnBlur = false,
  unitAdornment = false,
  textFieldProps = {},
  ...numberFormatProps
}: Props<FormValues>) => {
  const [isFocused, setIsFocused] = useState(false);
  const isMouseFocusing = useRef(false);

  const handleChange = (onFieldChange: any, { value }: NumberFormatValues, sourceInfo: SourceInfo) => {
    const parsedValue = parseFloat(value);

    if (sourceInfo.source === 'event') {
      if (customOnChange) {
        customOnChange(isNaN(parsedValue) ? 0 : parsedValue, name);
      } else {
        if (isNaN(parsedValue)) return onFieldChange(null);
        onFieldChange(parsedValue);
      }
    } else {
      onFieldChange(isNaN(parsedValue) ? null : parsedValue);
    }
  };

  const getFormatOnBlurProps = (onFieldBlur: () => void) => ({
    fixedDecimalScale: !isFocused,
    onMouseDown: (event: React.MouseEvent) => {
      // A click that focuses the input would collapse the selection made in onFocus on mouseup
      isMouseFocusing.current = event.target instanceof HTMLInputElement && document.activeElement !== event.target;
    },
    onMouseUp: (event: React.MouseEvent) => {
      if (!isMouseFocusing.current) return;
      isMouseFocusing.current = false;
      event.preventDefault();
    },
    onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      event.target.select();
    },
    onBlur: () => {
      setIsFocused(false);
      isMouseFocusing.current = false;
      onFieldBlur();
    },
  });

  const unitAdornmentProps = {
    suffix: undefined,
    InputProps: {
      ...(textFieldProps as any)?.InputProps,
      endAdornment: <InputAdornment position="end">%</InputAdornment>,
    },
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, value, onChange, ...field }, fieldState }) => (
        <NumberFormat
          suffix="%"
          fullWidth
          size="small"
          label={label}
          allowNegative
          inputRef={ref}
          decimalScale={2}
          thousandSeparator
          fixedDecimalScale
          variant="standard"
          customInput={TextField}
          {...field}
          {...(textFieldProps as any)}
          {...numberFormatProps}
          {...(formatOnBlur ? getFormatOnBlurProps(field.onBlur) : {})}
          {...(unitAdornment ? unitAdornmentProps : {})}
          inputProps={{
            ...(textFieldProps as any)?.inputProps,
            ...(dataTestId ? { 'data-testid': dataTestId } : {}),
          }}
          error={!!fieldState.error}
          helperText={fieldState.error ? fieldState.error.message : ''}
          value={formatOnBlur && (value === null || value === undefined) ? '' : parseFloat(value)}
          onValueChange={(values, sourceInfo) => handleChange(onChange, values, sourceInfo)}
        />
      )}
    />
  );
};
